import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Filter, Database, Radio, Info, Trash2, Zap } from "lucide-react";

const NAVY = "#0B2A6B";
const GOLD = "#D4A24C";

function Bar({ label, users, conv, drop, max, top }) {
  const width = max ? Math.max((users / max) * 100, users > 0 ? 6 : 2) : 2;
  return (
    <div data-testid={`funnel-stage-${label.toLowerCase().replace(/\s+/g, "-")}`} className="group">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className="text-slate-500">
          <b className="text-slate-800">{users.toLocaleString()}</b> users · {conv}% of top
          {!top && <span className={`ml-2 ${drop > 50 ? "text-red-600 font-bold" : "text-amber-600"}`}>▼ {drop}% drop</span>}
        </span>
      </div>
      <div className="h-7 w-full overflow-hidden rounded-lg bg-slate-100">
        <div className="flex h-full items-center rounded-lg text-[10px] font-bold text-white transition-all"
             style={{ width: `${width}%`, background: `linear-gradient(90deg, ${NAVY}, #1e4ba8)` }}>
          <span className="pl-2">{users > 0 ? users.toLocaleString() : ""}</span>
        </div>
      </div>
    </div>
  );
}

function TopList({ title, items, valueKey = "count", nameKey = "name", suffix = "" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h4>
      {(!items || items.length === 0) ? (
        <p className="text-xs text-slate-400">No data yet</p>
      ) : (
        <ul className="space-y-1.5">
          {items.slice(0, 6).map((it, i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span className="truncate text-slate-700">{it[nameKey] || "—"}</span>
              <span className="ml-2 shrink-0 rounded bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">{it[valueKey]}{suffix}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const KPIS = [
  ["Total Visitors", "totalVisitors", ""], ["Total Leads", "totalLeads", ""],
  ["Visitor → Lead", "visitorToLeadPct", "%"], ["AI Start Rate", "aiStartRatePct", "%"],
  ["AI Completion", "aiCompletionRatePct", "%"], ["Lead Submission", "leadSubmissionRatePct", "%"],
  ["Expert Click Rate", "expertClickRatePct", "%"], ["Checkout Rate", "checkoutRatePct", "%"],
  ["Payment Success", "paymentSuccessRatePct", "%"], ["Visitor → Customer", "overallVisitorToCustomerPct", "%"],
];

export default function FunnelAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [cleaning, setCleaning] = useState(false);

  const load = () => api.get(`/admin/analytics/conversion-funnel?days=${days}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { setData(null); load(); }, [days]);

  const cleanupTestLeads = async () => {
    const prefixes = ["TEST_", "PIPELINE_", "QA_", "DIAG_", "PIPELINE"];
    try {
      const dry = await api.post("/admin/leads/bulk-delete", { prefixes, dryRun: true });
      if (!dry.data.matched) { toast.info("No test leads found."); return; }
      if (!window.confirm(`Delete ${dry.data.matched} test lead(s)? (${dry.data.sample.map((s) => s.name).slice(0, 5).join(", ")}…)`)) return;
      setCleaning(true);
      const res = await api.post("/admin/leads/bulk-delete", { prefixes });
      toast.success(`Deleted ${res.data.deleted} test lead(s).`);
      load();
    } catch { toast.error("Cleanup failed."); }
    finally { setCleaning(false); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0B2A6B]" /></div>;

  const maxUsers = Math.max(1, ...data.funnel.map((s) => s.users));
  const g = data.growth || {};
  const ds = data.dataSources || {};

  return (
    <section data-testid="conversion-funnel-section" className="space-y-6">
      <div className="flex flex-col gap-3 border-t border-slate-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-extrabold text-slate-900">Conversion Funnel</h2>
          <p className="text-sm text-slate-500">Where visitors drop off before becoming paying customers.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cleanup-test-leads" onClick={cleanupTestLeads} disabled={cleaning}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50">
            {cleaning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Clean Test Leads
          </button>
          <Filter className="h-4 w-4 text-slate-400" />
          <select data-testid="funnel-range-select" value={days} onChange={(e) => setDays(Number(e.target.value))}
                  className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#0B2A6B]">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </select>
        </div>
      </div>

      {/* Data source legend */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-emerald-700 ring-1 ring-emerald-200"><Radio className="h-3 w-3" /> Live first-party (deploy-forward)</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-blue-700 ring-1 ring-blue-200"><Database className="h-3 w-3" /> Historical DB (all-time: leads & payments)</span>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ring-1 ${data.ga4?.configured ? "bg-amber-50 text-amber-700 ring-amber-200" : "bg-slate-100 text-slate-500 ring-slate-200"}`}>
          <Info className="h-3 w-3" /> GA4 backfill: {data.ga4?.configured ? "connected" : "not connected"}
        </span>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {KPIS.map(([label, key, suf]) => (
          <div key={key} data-testid={`funnel-kpi-${key}`} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="mt-1 font-display text-2xl font-extrabold" style={{ color: NAVY }}>{data.kpis[key]}{suf}</p>
          </div>
        ))}
      </div>

      {/* Growth & AI funnel health */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display font-bold text-slate-900"><Zap className="h-4 w-4 text-[#D4A24C]" /> AI Funnel Health & Growth</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {[
            ["AI Started", g.aiStarted ?? 0], ["AI Completed", g.aiCompleted ?? 0],
            ["AI Drop-off", `${g.aiDropOffPct ?? 0}%`],
            ["Avg Completion", g.avgCompletionSeconds != null ? `${g.avgCompletionSeconds}s` : "—"],
            ["Top CTA", g.topCTA ? g.topCTA.ctaId : "—"],
            ["Top Landing", g.topLandingPages?.[0]?.name || "—"],
            ["Top College", g.topCollegeViewed?.name || "—"],
          ].map(([label, val]) => (
            <div key={label} data-testid={`growth-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-xl bg-slate-50 p-3">
              <p className="text-[11px] text-slate-500">{label}</p>
              <p className="mt-0.5 truncate font-display text-lg font-extrabold text-slate-900">{val}</p>
            </div>
          ))}
        </div>
        {g.ctaPerformance && g.ctaPerformance.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">CTA / A-B Performance</p>
            <table className="w-full min-w-[520px] text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th className="pb-2">CTA</th><th className="pb-2">Variant</th><th className="pb-2">Impressions</th><th className="pb-2">Clicks</th><th className="pb-2">CTR</th></tr></thead>
              <tbody>
                {g.ctaPerformance.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100"><td className="py-1.5 font-medium text-slate-700">{r.ctaId}</td><td className="text-slate-500">{r.variant}</td><td>{r.impressions}</td><td>{r.clicks}</td><td className="font-bold" style={{ color: NAVY }}>{r.ctrPct}%</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Visual funnel */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display font-bold text-slate-900">Funnel Stages</h3>
          <span className="text-xs text-slate-400">{data.eventsInRange.toLocaleString()} events in range</span>
        </div>
        <div className="space-y-3">
          {data.funnel.map((s, i) => (
            <Bar key={s.stage} label={s.stage} users={s.users} conv={s.conversionFromTop} drop={s.dropFromPrev} max={maxUsers} top={i === 0} />
          ))}
        </div>
        {data.funnel[0].users === 0 && (
          <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
            Top-of-funnel stages are collected first-party from deployment onward — they populate as live traffic arrives. Lead & payment stages below reflect all-time database records.
          </p>
        )}
      </div>

      {/* Breakdowns */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <TopList title="Leads by State" items={data.breakdowns.leadsByState} />
        <TopList title="Leads by City" items={data.breakdowns.leadsByCity} />
        <TopList title="Leads by Course" items={data.breakdowns.leadsByCourse} />
        <TopList title="Leads by Source" items={data.breakdowns.leadsBySource} />
        <TopList title="Events by Traffic Source" items={data.breakdowns.bySource} />
        <TopList title="Events by Device" items={data.breakdowns.byDevice} />
      </div>

      {/* New vs returning + top insights */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">New vs Returning</h4>
          <div className="flex gap-4">
            <div><p className="font-display text-2xl font-extrabold text-slate-900">{data.breakdowns.newVsReturning.new}</p><p className="text-xs text-slate-500">New</p></div>
            <div><p className="font-display text-2xl font-extrabold" style={{ color: GOLD }}>{data.breakdowns.newVsReturning.returning}</p><p className="text-xs text-slate-500">Returning</p></div>
          </div>
        </div>
        <TopList title="Top Searched Colleges" items={data.insights.topSearchedColleges} />
        <TopList title="Top Searched Courses" items={data.insights.topSearchedCourses} />
      </div>

      {/* Highest converting source */}
      {data.insights.convBySource && data.insights.convBySource.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-3 font-display font-bold text-slate-900">Conversion by Traffic Source (sessions → leads)</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th className="pb-2">Source</th><th className="pb-2">Sessions</th><th className="pb-2">Leads</th><th className="pb-2">Conv %</th></tr></thead>
              <tbody>
                {data.insights.convBySource.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100"><td className="py-1.5 font-medium text-slate-700">{r.source}</td><td>{r.sessions}</td><td>{r.leads}</td><td className="font-bold" style={{ color: NAVY }}>{r.convPct}%</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
